import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2.5 whitespace-nowrap text-sm font-semibold ring-offset-background transition-all duration-250 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 font-heading tracking-wide",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/92 rounded-lg shadow-[0_1px_3px_hsl(var(--primary)/0.2)] hover:shadow-[0_4px_12px_hsl(var(--primary)/0.25)]",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-lg",
        outline: "border border-border bg-card hover:bg-muted text-foreground rounded-lg",
        secondary: "bg-secondary text-secondary-foreground hover:bg-navy-light rounded-lg shadow-[0_1px_3px_hsl(var(--navy)/0.15)] hover:shadow-[0_4px_12px_hsl(var(--navy)/0.2)]",
        ghost: "hover:bg-muted/60 text-foreground rounded-lg",
        link: "text-primary underline-offset-4 hover:underline p-0 h-auto",
        hero: "bg-primary text-primary-foreground hover:bg-primary/92 rounded-lg shadow-[0_2px_8px_hsl(var(--primary)/0.25)] hover:shadow-[0_8px_24px_hsl(var(--primary)/0.3)] hover:-translate-y-[1px] active:translate-y-0",
        heroOutline: "border-2 border-border bg-transparent text-foreground hover:bg-secondary hover:text-secondary-foreground rounded-lg hover:-translate-y-[1px] active:translate-y-0",
        cta: "bg-primary text-primary-foreground hover:bg-primary/92 rounded-lg shadow-[0_2px_8px_hsl(var(--primary)/0.25)] hover:shadow-[0_8px_24px_hsl(var(--primary)/0.3)] hover:-translate-y-[1px] active:translate-y-0",
        navCta: "bg-primary text-primary-foreground hover:bg-primary/92 rounded-lg text-xs shadow-[0_1px_3px_hsl(var(--primary)/0.2)]",
        ctaNavy: "bg-secondary text-secondary-foreground hover:bg-navy-light rounded-lg shadow-[0_2px_8px_hsl(var(--navy)/0.2)] hover:shadow-[0_8px_24px_hsl(var(--navy)/0.25)] hover:-translate-y-[1px] active:translate-y-0",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 px-4 text-xs",
        lg: "h-12 px-8 text-[0.9375rem]",
        xl: "h-14 px-10 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
